import React from 'react';
import { Channel, ChannelType, EmailMailbox, AiSmartFolder } from './types';
import {
  channelAuthStatus,
  emailConnectionLabel,
  resolveEmailAuthMode
} from './utils/emailChannel';
import EmailMailboxNav from './email/EmailMailboxNav';
import AiSmartInboxNav from './ai/AiSmartInboxNav';
import './Communications.css';

const DEFAULT_SMART_FOLDERS: AiSmartFolder[] = [
  { id: 'focus', name: 'Focus' },
  { id: 'news', name: 'News' },
  { id: 'other', name: 'Other' }
];

interface ChannelNavProps {
  channels: Channel[];
  loading: boolean;
  error: string | null;
  selectedChannelId: number | null;
  expandedChannelIds: Set<number>;
  onCollapseChannel: (channelId: number) => void;
  mailboxesByChannelId: Record<number, EmailMailbox[]>;
  smartFoldersByChannelId: Record<number, AiSmartFolder[]>;
  selectedMailbox: string;
  selectedFolder: string;
  mailboxesLoading: boolean;
  mailboxesError: string | null;
  smartFoldersLoading: boolean;
  smartFoldersError: string | null;
  onSelectChannel: (channel: Channel) => void;
  onSelectChannelMailbox: (channel: Channel, mailbox: string) => void;
  onSelectChannelFolder: (channel: Channel, folderId: string) => void;
  onEditChannel: (channel: Channel) => void;
  onDeleteChannel: (channel: Channel) => void;
  onAddChannel: () => void;
}

const CHANNEL_ICONS: Record<ChannelType, string> = {
  email: '✉',
  sms: '💬',
  chat: '🗨',
  ai: '✦'
};

const ChannelNav: React.FC<ChannelNavProps> = ({
  channels,
  loading,
  error,
  selectedChannelId,
  expandedChannelIds,
  onCollapseChannel,
  mailboxesByChannelId,
  smartFoldersByChannelId,
  selectedMailbox,
  selectedFolder,
  mailboxesLoading,
  mailboxesError,
  smartFoldersLoading,
  smartFoldersError,
  onSelectChannel,
  onSelectChannelMailbox,
  onSelectChannelFolder,
  onEditChannel,
  onDeleteChannel,
  onAddChannel
}) => {
  const handleEdit = (e: React.MouseEvent, channel: Channel) => {
    e.stopPropagation();
    onEditChannel(channel);
  };

  const handleDelete = (e: React.MouseEvent, channel: Channel) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Delete channel "${channel.name}"? This cannot be undone.`
    );
    if (confirmed) {
      onDeleteChannel(channel);
    }
  };

  return (
    <div className="channel-nav">
      <div className="channel-nav-header">
        <h3>Channels</h3>
        <button
          className="app-btn app-btn-primary app-btn-small channel-nav-add-btn"
          onClick={onAddChannel}
          title="Add Channel"
        >
          +
        </button>
      </div>

      {error && <div className="app-error-message">{error}</div>}

      {loading && <div className="channel-nav-loading">Loading...</div>}

      <ul className="channel-nav-list">
        {channels.map(channel => {
          const isSelected = selectedChannelId === channel.id;
          const isExpanded = expandedChannelIds.has(channel.id);
          const hasFolderNav = channel.type === 'email' || channel.type === 'ai';
          const channelMailboxes = mailboxesByChannelId[channel.id] ?? [];
          const channelSmartFolders = smartFoldersByChannelId[channel.id] ?? DEFAULT_SMART_FOLDERS;
          const showChannelMailboxes = channel.type === 'email' && isExpanded;
          const showChannelSmartFolders = channel.type === 'ai' && isExpanded;
          const emailAuthMode = channel.type === 'email' ? resolveEmailAuthMode(channel) : null;
          const connectionLabel = emailAuthMode ? emailConnectionLabel(emailAuthMode) : null;

          return (
            <li key={channel.id} className="channel-nav-group">
              <div
                className={`channel-nav-item${isSelected ? ' active' : ''}`}
                onClick={() => onSelectChannel(channel)}
              >
                <span className="channel-nav-icon" aria-hidden="true">
                  {CHANNEL_ICONS[channel.type] || '📡'}
                </span>
                {hasFolderNav && isExpanded && (
                  <button
                    type="button"
                    className="channel-nav-collapse"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCollapseChannel(channel.id);
                    }}
                    title={channel.type === 'ai' ? 'Hide smart inboxes' : 'Hide folders'}
                    aria-expanded
                  >
                    ▾
                  </button>
                )}
                <span className="channel-nav-name">{channel.name}</span>
                {(() => {
                  const authStatus = channelAuthStatus(channel);
                  if (!authStatus) return null;
                  if (authStatus === 'needs-auth') {
                    return (
                      <span
                        className="channel-nav-auth channel-nav-auth-needs"
                        title="Authorization required — select channel or open settings to authorize"
                        aria-label="Authorization required"
                      >
                        <span className="channel-nav-auth-glyph" aria-hidden="true">▲</span>
                      </span>
                    );
                  }
                  return (
                    <span
                      className="channel-nav-auth channel-nav-auth-ok"
                      title={
                        channel.oauthEmail
                          ? `Authorized as ${channel.oauthEmail}`
                          : 'Authorized'
                      }
                      aria-label="Authorized"
                    />
                  );
                })()}
                {connectionLabel && (
                  <span
                    className={`channel-nav-connection${
                      channelAuthStatus(channel) === 'authorized' ? ' connected' : ''
                    }`}
                    title={
                      channel.oauthEmail
                        ? `Connected as ${channel.oauthEmail}`
                        : `${connectionLabel} — ${
                            channelAuthStatus(channel) === 'authorized'
                              ? 'authorized'
                              : emailAuthMode === 'imap'
                                ? 'password'
                                : 'authorize in settings'
                          }`
                    }
                  >
                    {connectionLabel}
                  </span>
                )}
                <div className="channel-nav-actions">
                  <button
                    className="channel-nav-edit"
                    onClick={(e) => handleEdit(e, channel)}
                    title={`Edit ${channel.name}`}
                  >
                    ✎
                  </button>
                  <button
                    className="channel-nav-delete"
                    onClick={(e) => handleDelete(e, channel)}
                    title={`Delete ${channel.name}`}
                  >
                    ×
                  </button>
                </div>
              </div>
              {showChannelMailboxes && (
                <EmailMailboxNav
                  nested
                  mailboxes={channelMailboxes}
                  selectedMailbox={isSelected ? selectedMailbox : ''}
                  loading={isSelected && mailboxesLoading}
                  error={isSelected ? mailboxesError : null}
                  onSelectMailbox={(path) => onSelectChannelMailbox(channel, path)}
                />
              )}
              {showChannelSmartFolders && (
                <AiSmartInboxNav
                  nested
                  folders={channelSmartFolders}
                  selectedFolder={isSelected ? selectedFolder : ''}
                  loading={isSelected && smartFoldersLoading}
                  error={isSelected ? smartFoldersError : null}
                  onSelectFolder={(folderId) => onSelectChannelFolder(channel, folderId)}
                />
              )}
            </li>
          );
        })}

        {!loading && channels.length === 0 && (
          <li className="channel-nav-empty">No channels yet</li>
        )}
      </ul>
    </div>
  );
};

export default ChannelNav;
